import log from "loglevel";
import {ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState} from "react";
import {throttle} from "lodash";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {Box, IconButton, TextField} from "@mui/material";

import {Colors, Container, IconAttachment, IconSend} from "@bounce/shared/ui";
import {AppState} from "../store/definitions";
import {FilePreview} from "./FilePreview";
import {attachFiles, detachFiles} from "../store/actions/genericActions";
import {CHAR_LIMIT} from "../constants";
import {validateFiles} from "../utils/validateFiles";

const EMPTY_ARRAY: File[] = [];

export const MessageInput = () => {
  const dispatch = useDispatch();
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const {conversation, attachedFilesFromState, fileAttachmentConfig} = useSelector(
    (state: AppState) => ({
      conversation: state.chat.conversation,
      attachedFilesFromState: state.chat.attachedFiles,
      fileAttachmentConfig: state.config.fileAttachment,
    }),
    shallowEqual
  );
  const attachedFiles = attachedFilesFromState || EMPTY_ARRAY;
  const oldAttachmentsLength = useRef((attachedFiles || []).length);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsBoxRef = useRef<HTMLDivElement>(null);

  const throttleChange = useMemo(
    () =>
      throttle(() => {
        conversation?.typing();

        // in case the input was already focused, let's make sure to send the `read` status if the customer is typing
        if (conversation?.lastReadMessageIndex !== conversation?.lastMessage?.index) {
          conversation?.setAllMessagesRead();
        }
      }, 500),
    [conversation]
  );

  const isSubmitDisabled = (!text.trim() && !attachedFiles?.length) || isSending;

  const send = async () => {
    if (isSubmitDisabled) {
      return;
    }
    if (!conversation) {
      log.error("Failed sending message: no conversation found");
      return;
    }
    setIsSending(true);

    let preparedMessage = conversation.prepareMessage();
    preparedMessage = preparedMessage.setBody(text);
    attachedFiles.forEach((file: File) => {
      const formData = new FormData();
      formData.append(file.name, file);
      preparedMessage.addMedia(formData);
    });
    await preparedMessage.build().send();
    setText("");
    dispatch(detachFiles(attachedFiles));
    setIsSending(false);
    textAreaRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLInputElement).form?.dispatchEvent(new Event("submit", {cancelable: true, bubbles: true}));
    }
  };

  const onChange = (val: ChangeEvent<HTMLTextAreaElement>) => {
    setText(val.target.value);

    throttleChange();
  };

  const onFocus = () => {
    conversation?.setAllMessagesRead();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files && Array.from(event.target.files);
    if (selectedFiles) {
      const validFiles = validateFiles(selectedFiles, dispatch, attachedFiles, fileAttachmentConfig);
      dispatch(attachFiles(validFiles));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    textAreaRef.current?.focus();
  };

  useEffect(() => {
    textAreaRef.current?.setAttribute("rows", "1");
    textAreaRef.current?.focus();
  }, [textAreaRef]);

  // Ensuring attached files are automatically scrolled to within the attachments container
  useEffect(() => {
    if (!attachmentsBoxRef.current) {
      return;
    }

    if (attachedFiles.length > oldAttachmentsLength.current) {
      // Use scrollTop to scroll within the container only, not the entire page
      attachmentsBoxRef.current.scrollTop = attachmentsBoxRef.current.scrollHeight;
    }

    oldAttachmentsLength.current = attachedFiles.length;
  }, [attachedFiles]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
      style={{flexShrink: 0}}
    >
      <Container
        sx={{
          backgroundColor: Colors.grey._100,
          borderRadius: "8px",
          p: "8px",
          m: "8px",
          opacity: isSending ? 0.6 : 1,
          gap: "4px",
          alignItems: "stretch",
        }}
      >
        {attachedFiles && attachedFiles.length > 0 && (
          <Box
            ref={attachmentsBoxRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              maxHeight: "300px",
              position: "relative",
              gap: "4px",
              alignItems: "stretch",
            }}
          >
            {attachedFiles.map((file, index) => (
              <FilePreview focusable={true} key={index} file={file} isBubble={false} disabled={isSending} />
            ))}
          </Box>
        )}
        <TextField
          inputRef={textAreaRef}
          placeholder="Type your message"
          value={text}
          onChange={onChange}
          onFocus={onFocus}
          disabled={isSending}
          onKeyDown={onKeyDown}
          multiline
          maxRows={4}
          fullWidth
          variant="standard"
          slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                fontSize: "14px",
                lineHeight: "22px",
                color: Colors.grey._900,
                "& ::placeholder": {
                  color: Colors.grey._500,
                  opacity: 1,
                },
              },
            },
            htmlInput: {
              maxLength: CHAR_LIMIT,
            },
          }}
        />
        <Container row sx={{justifyContent: "space-between", alignItems: "center", gap: 0}}>
          {fileAttachmentConfig?.enabled ? (
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add file attachment"
              sx={{p: 0, color: Colors.grey._600}}
            >
              <input
                style={{display: "none"}}
                onChange={onFileChange}
                type="file"
                accept={
                  fileAttachmentConfig?.acceptedExtensions &&
                  fileAttachmentConfig.acceptedExtensions.map((e) => `.${e}`).join(",")
                }
                ref={fileInputRef}
                multiple
              />
              <IconAttachment style={{width: 20, height: 20}} />
            </IconButton>
          ) : (
            <Box />
          )}
          <IconButton
            type="submit"
            disabled={isSubmitDisabled}
            aria-label="Send message"
            sx={{
              width: 32,
              height: 32,
              backgroundColor: isSubmitDisabled ? Colors.grey._400 : Colors.primary._800,
              borderRadius: "50%",
              "&:hover": {
                backgroundColor: isSubmitDisabled ? Colors.grey._400 : Colors.primary._900,
              },
              "&.Mui-disabled": {
                backgroundColor: Colors.grey._400,
              },
            }}
          >
            <IconSend style={{width: 20, height: 20, color: Colors.bw.white}} />
          </IconButton>
        </Container>
      </Container>
    </form>
  );
};
